import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const _color = new THREE.Color();

/** Bake a flat colour into a geometry's vertex-colour attribute. */
export function paint(geometry, color) {
  _color.set(color);
  const count = geometry.attributes.position.count;
  const arr = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    arr[i * 3] = _color.r;
    arr[i * 3 + 1] = _color.g;
    arr[i * 3 + 2] = _color.b;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return geometry;
}

/** A coloured box, positioned by its centre. */
export function box(w, h, d, color, x = 0, y = 0, z = 0) {
  const g = new THREE.BoxGeometry(w, h, d);
  g.translate(x, y, z);
  return paint(g, color);
}

/**
 * Merge parts into one geometry and dispose the parts.
 * Every voxel prop in the game is a single draw call because of this.
 */
export function weld(parts) {
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  return merged;
}

/** Shared materials — reused everywhere to keep the material count tiny. */
export function makeMaterials(clippingPlanes) {
  const solid = new THREE.MeshLambertMaterial({
    vertexColors: true,
    clippingPlanes,
  });
  // Unclipped variant for scenery that must never be cut at the field edge.
  const scenery = new THREE.MeshLambertMaterial({ vertexColors: true });
  // Fake contact shadow — the player is small and needs anchoring to the ground.
  const shadow = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.32,
    depthWrite: false,
  });
  return { solid, scenery, shadow };
}

/** Deterministic-ish PRNG so a level looks varied but replays identically. */
export function makeRng(seed) {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
}

export function randRange(rng, [min, max]) {
  return min + rng() * (max - min);
}

export function randInt(rng, [min, max]) {
  return Math.floor(min + rng() * (max - min + 1));
}
